/**
 * ZAXIA Data Provider
 * Provides API data in format compatible with existing components
 * Bridges backend API types to frontend types
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  fetchHotel,
  fetchSession,
  fetchBookings,
  fetchCalendarEvents,
  fetchBill,
  createBooking,
  type ApiHotel,
  type ApiSession,
  type ApiBooking,
  type ApiCalendarEvent,
  type ApiBill,
  type CreateBookingRequest,
} from '@/api';
import type {
  Hotel,
  Guest,
  Room,
  Stay,
  Booking,
  CalendarEvent,
  HotelBill,
  BillCharge,
} from '@/types';
import {
  RoomType,
  RoomStatus,
  StayStatus,
  BookingStatus,
  CalendarEventStatus,
  ChargeCategory,
  ServiceCategory,
} from '@/types';

// =============================================================================
// TRANSFORMERS - Convert API types to frontend types
// =============================================================================

function transformHotel(api: ApiHotel): Hotel {
  return {
    hotelId: api.hotelId,
    name: api.name,
    timezone: api.timezone,
    currency: api.currency,
    defaultLanguage: api.defaultLanguage,
    languages: api.languages,
    operatingHours: api.operatingHours,
    policies: api.policies,
  };
}

function transformGuest(api: ApiSession['guest']): Guest {
  return {
    guestId: api.guestId,
    firstName: api.firstName,
    lastName: api.lastName,
    email: api.email || '',
    phone: api.phone || '',
    language: api.language,
    preferences: api.preferences,
  };
}

function transformRoom(api: ApiSession['room']): Room {
  return {
    roomId: api.roomId,
    hotelId: api.hotelId,
    roomNumber: api.roomNumber,
    roomType: api.roomType as RoomType,
    floor: api.floor,
    status: api.status as RoomStatus,
    tvId: api.tvId,
    currentStayId: api.currentStayId,
  };
}

function transformStay(api: ApiSession['stay']): Stay {
  return {
    stayId: api.stayId,
    hotelId: api.hotelId,
    roomId: api.roomId,
    guestId: api.guestId,
    checkInDate: api.checkInDate,
    checkOutDate: api.checkOutDate,
    actualCheckIn: api.actualCheckIn,
    actualCheckOut: api.actualCheckOut,
    status: api.status as StayStatus,
    greenPreferences: {
      greenStayOptIn: api.greenPreferences.greenStayOptIn,
      // Phase D4.3: Normalize date strings to YYYY-MM-DD format (backend may return full ISO timestamps)
      skipCleaningDates: (api.greenPreferences.skipCleaningDates || []).map(d => d.split('T')[0]!),
      towelsOnlyDates: (api.greenPreferences.towelsOnlyDates || []).map(d => d.split('T')[0]!),
    },
  };
}

function transformBooking(api: ApiBooking): Booking {
  return {
    bookingId: api.bookingId,
    stayId: api.stayId,
    serviceId: api.serviceId,
    serviceName: api.serviceName,
    category: api.category as ServiceCategory,
    datetime: api.datetime,
    durationMinutes: api.durationMinutes,
    status: api.status as BookingStatus,
    price: api.price,
    location: api.location ?? undefined,
    notes: api.notes ?? undefined,
    createdAt: api.createdAt,
  };
}

function transformCalendarEvent(api: ApiCalendarEvent): CalendarEvent {
  return {
    eventId: api.eventId,
    stayId: api.stayId,
    serviceType: api.serviceType as ServiceCategory,
    title: api.title,
    datetime: api.datetime,
    durationMinutes: api.durationMinutes,
    location: api.location,
    sourceBookingId: api.sourceBookingId,
    sourceTaskId: api.sourceTaskId,
    status: api.status as CalendarEventStatus,
  };
}

// Map service categories to bill charge categories
function mapToChargeCategory(category: string): ChargeCategory {
  const mapping: Record<string, ChargeCategory> = {
    'ROOM': ChargeCategory.ROOM,
    'SPA': ChargeCategory.SPA,
    'RESTAURANT': ChargeCategory.RESTAURANT,
    'BAR': ChargeCategory.BAR,
    'ROOM_SERVICE': ChargeCategory.ROOM_SERVICE,
    'MINIBAR': ChargeCategory.MINIBAR,
    'ACTIVITY': ChargeCategory.ACTIVITY,
    'TRANSPORT': ChargeCategory.TRANSPORT,
    // HOTEL_INFO services (Gym, Pool, etc.) go to OTHER
    'HOTEL_INFO': ChargeCategory.OTHER,
    'HOUSEKEEPING': ChargeCategory.OTHER,
  };
  return mapping[category] || ChargeCategory.OTHER;
}

function transformBill(api: ApiBill): HotelBill {
  const charges: BillCharge[] = api.charges.map(c => ({
    chargeId: c.chargeId,
    stayId: c.stayId,
    category: mapToChargeCategory(c.category),
    description: c.description,
    notes: c.notes,
    amount: c.amount,
    datetime: c.datetime,
    bookingId: c.bookingId,
  }));

  return {
    stayId: api.stayId,
    guestName: api.guestName,
    roomNumber: api.roomNumber,
    checkInDate: api.checkInDate,
    checkOutDate: api.checkOutDate,
    charges,
    totalAmount: api.totalAmount,
    currency: api.currency,
  };
}

// =============================================================================
// CONTEXT
// =============================================================================

interface DataContextValue {
  // Data
  hotel: Hotel | null;
  guest: Guest | null;
  room: Room | null;
  stay: Stay | null;
  onboardingCompleted: boolean;
  bookings: Booking[];
  calendarEvents: CalendarEvent[];
  bill: HotelBill | null;

  // Loading states
  loading: boolean;
  error: Error | null;

  // Actions
  reload: () => void;
  addBooking: (data: CreateBookingRequest) => Promise<Booking>;
  refreshBookings: () => void;
  refreshBill: () => void;
  refreshCalendar: () => void;
}

const DataContext = createContext<DataContextValue | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

interface DataProviderProps {
  children: ReactNode;
}

export function DataProvider({ children }: DataProviderProps) {
  // State
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [guest, setGuest] = useState<Guest | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [stay, setStay] = useState<Stay | null>(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean>(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [bill, setBill] = useState<HotelBill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  // Reload function - triggers a fresh data fetch
  const reload = useCallback(() => {
    console.log('[ZAXIA] Manual reload triggered');
    setReloadTrigger(prev => prev + 1);
  }, []);

  // Initial data fetch (and on reload)
  useEffect(() => {
    async function loadInitialData() {
      try {
        setLoading(true);
        setError(null);

        // Fetch all data in parallel
        const [hotelData, sessionData, bookingsData, calendarData, billData] = await Promise.all([
          fetchHotel(),
          fetchSession(),
          fetchBookings(),
          fetchCalendarEvents(),
          fetchBill(),
        ]);

        // Transform and set data
        setHotel(transformHotel(hotelData));
        setGuest(transformGuest(sessionData.guest));
        setRoom(transformRoom(sessionData.room));
        setStay(transformStay(sessionData.stay));
        setOnboardingCompleted(sessionData.onboardingCompleted);
        setBookings(bookingsData.map(transformBooking));
        setCalendarEvents(calendarData.map(transformCalendarEvent));
        setBill(transformBill(billData));

        console.log('[ZAXIA] Data loaded from backend');
      } catch (e) {
        console.error('[ZAXIA] Failed to load data:', e);
        setError(e instanceof Error ? e : new Error('Failed to load data'));
      } finally {
        setLoading(false);
      }
    }

    loadInitialData();
  }, [reloadTrigger]);

  // Refresh functions
  const refreshBookings = useCallback(async () => {
    try {
      const data = await fetchBookings();
      setBookings(data.map(transformBooking));
    } catch (e) {
      console.error('[ZAXIA] Failed to refresh bookings:', e);
    }
  }, []);

  const refreshBill = useCallback(async () => {
    try {
      const data = await fetchBill();
      setBill(transformBill(data));
    } catch (e) {
      console.error('[ZAXIA] Failed to refresh bill:', e);
    }
  }, []);

  const refreshCalendar = useCallback(async () => {
    try {
      const data = await fetchCalendarEvents();
      setCalendarEvents(data.map(transformCalendarEvent));
    } catch (e) {
      console.error('[ZAXIA] Failed to refresh calendar:', e);
    }
  }, []);

  // Auto-refresh bookings + calendar every 60 s so staff changes
  // (reschedules, cancellations) appear in the guest view automatically.
  useEffect(() => {
    const tick = setInterval(() => {
      refreshBookings();
      refreshCalendar();
    }, 60_000);
    return () => clearInterval(tick);
  }, [refreshBookings, refreshCalendar]);

  // Add booking
  const addBooking = useCallback(async (data: CreateBookingRequest): Promise<Booking> => {
    const apiBooking = await createBooking(data);
    const booking = transformBooking(apiBooking);

    // Refresh all related data
    await Promise.all([
      refreshBookings(),
      refreshCalendar(),
      refreshBill(),
    ]);

    return booking;
  }, [refreshBookings, refreshCalendar, refreshBill]);

  const value: DataContextValue = {
    hotel,
    guest,
    room,
    stay,
    onboardingCompleted,
    bookings,
    calendarEvents,
    bill,
    loading,
    error,
    reload,
    addBooking,
    refreshBookings,
    refreshBill,
    refreshCalendar,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useData(): DataContextValue {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within DataProvider');
  }
  return context;
}
